import { useNodeProps } from '@/node-editor/hooks';
import { INode, INodeProps } from '@/types/node';
import {
  Box,
  CircularProgress,
  Grid,
  LinearProgress,
  Link,
} from '@mui/material';
import { useTheme, Theme } from '@mui/material/styles';
import axios from 'axios';
import React, { useContext, useEffect, useState } from 'react';
import { IContent, IVisitorGoal } from './contract';
import UserAvatar from '@/components/ui/userAvatar';
import { AuthContext } from '@/store';

interface INodeExtended extends Omit<INode, 'content'> {
  content: IContent;
}

interface IProps extends Omit<INodeProps, 'node'> {
  node: INodeExtended;
}

interface VisitorGoals {
  infoCount: number;
  followCount: number;
  discussionCount: number;
  messageCount: number;
  chatCount: number;
  threadCount: number;
  entityvisitsCount: number;
}
interface IGoalDraw {
  data: IVisitorGoal;
  count: number;
  url: string;
}

interface IContainerStyleProps {
  override: Record<string, any>;
  design: string;
}

const useGoalStyles = () => ({
  line: {
    height: '8px !important',
    borderRadius: '4px !important',
  },
  bar: {
    borderRadius: '4px !important',
  },
});

const Goal = ({ data, count, url }: IGoalDraw) => {
  const classes = useGoalStyles();
  return (
    <Box>
      <Box display="flex">
        <Box flexGrow={1}>
          <Link color="primary" href={data.url || url}>
            {data.text} &gt;
          </Link>
        </Box>
        <Box>
          {count}/{data.count}
        </Box>
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, (100 * count) / data.count)}
        sx={{
          ...classes.line,
          '& .MuiLinearProgress-bar': {
            ...classes.bar,
          },
        }}
      />
    </Box>
  );
};

const useContainerStyles = (
  theme: Theme,
  { override, design }: IContainerStyleProps
) => ({
  root: {
    width: '100% !important',
    display: 'flex !important',
    flexDirection: `${design === 'horizontal' ? 'row' : 'column'} !important`,
    alignItems: `${
      design === 'horizontal' ? 'flex-start' : 'center'
    } !important`,
    [`${theme.breakpoints.down('sm')}`]: {
      flexDirection: 'column !important',
      alignItems: 'center !important',
    },
    ...override.root,
  },
  item: {
    ...override.item,
  },
  empty: {
    ...override.empty,
  },
  head: {
    textAlign: 'center !important',
    padding: '8px 16px !important',
  },
  total: {
    position: 'relative !important',
    width: '100px !important',
    height: '100px !important',
    margin: 'auto !important',
  },
  avatar: {
    position: 'absolute !important',
    inset: '0 !important',
    padding: '10px !important',
  },
  totalholder: {
    position: 'absolute !important',
    inset: '0 !important',
    display: 'flex !important',
    alignItems: 'center !important',
    justifyContent: 'center !important',
  },
  totaltext: {
    backgroundColor: '#00000099 !important',
    borderRadius: '10px !important',
    color: '#fff !important',
    padding: '3px 8px !important',
    fontSize: '14px !important',
    height: '26px !important',
  },
});

export const Render = ({
  node: {
    id,
    override,
    content: {
      description,
      completed,
      design,
      info,
      follow,
      discussion,
      message,
      chat,
      thread,
      entityvisits,
    },
  },
}: IProps) => {
  const theme = useTheme();
  const classes = useContainerStyles(theme, {
    override,
    design,
  });
  const [goals, setGoals] = useState<IGoalDraw[]>([]);
  const { authState } = useContext(AuthContext);

  const reloadData = () => {
    axios
      .get(`/api/visitorgoals`)
      .then((response) => {
        const finished = response.data as VisitorGoals;
        const allGoals: IGoalDraw[] = [
          { data: info, url: '/profile/edit', count: finished.infoCount },
          {
            data: follow,
            url: '/live/participants',
            count: finished.followCount,
          },
          {
            data: discussion,
            url: '/live/discussion',
            count: finished.discussionCount,
          },
          {
            data: message,
            url: '/live/discussion',
            count: finished.messageCount,
          },
          { data: chat, url: '/live', count: finished.chatCount },
          {
            data: thread,
            url: '/live/discussion',
            count: finished.threadCount,
          },
          {
            data: entityvisits,
            url: '/live/collections',
            count: finished.entityvisitsCount,
          },
        ].filter((g) => g.data.count > 0);
        setGoals(allGoals);
      })
      .catch((err) => {
        console.log('Could not loaf goals');
      });
  };

  useEffect(() => {
    reloadData();
  }, []);

  const totalGoal = goals.reduce((tot, g) => tot + g.data.count, 0);
  const totalScore = goals.reduce(
    (tot, g) => tot + Math.min(g.count, g.data.count),
    0
  );
  const percent = totalGoal ? Math.round((100 * totalScore) / totalGoal) : 0;

  return (
    <Box sx={classes.root} {...useNodeProps(id)}>
      <Box sx={classes.head}>
        <Box sx={classes.total}>
          <CircularProgress
            variant="determinate"
            value={percent}
            size={100}
            thickness={3}
          />
          <Box sx={classes.avatar}>
            <UserAvatar
              customSize={{ width: 80, height: 80, fontSize: 20 }}
              user={authState.user}
            />
          </Box>
          <Box sx={classes.totalholder}>
            <Box sx={classes.totaltext}>{percent}%</Box>
          </Box>
        </Box>
        {percent === 100 ? completed : description}
      </Box>
      <Grid container spacing={1} sx={{ width: '100%' }}>
        {goals.length > 0 &&
          goals.map((g, index) => (
            <Grid
              key={index}
              item
              size={{ xs: 12, sm: design === 'horizontal' ? 6 : 12 }}
            >
              <Goal {...g} />
            </Grid>
          ))}
      </Grid>
    </Box>
  );
};
